import { getUrl } from './connection'
import { writable, derived } from 'svelte/store'
import { Buffer } from 'buffer/'
import {
    createLocalStorage, deleteLocalStorage, getLocalStorage, TOKEN_STORAGE_NAME, CLIENT_STORAGE_NAME
} from './storageUtils'
import { loading } from './stores/loading'
import { alert } from './stores/alert'

const token2user = _token => JSON.parse(Buffer.from(_token, 'base64').toString())


const removeToken = () => {
    token.set(null)
    deleteLocalStorage(TOKEN_STORAGE_NAME)
}

/**
 * Save the JWT in a cookie and in the store
 * @param {string} tokenString JWT token of logged user
 * @param {boolean} rememberUser The user will be remembered in a cookie
 */
const saveToken = (tokenString, rememberUser) => {
    createLocalStorage({ name: TOKEN_STORAGE_NAME, value: tokenString })
    token.set(getToken())
}

const getToken = () => getLocalStorage(TOKEN_STORAGE_NAME)

const token = writable(getToken())

function getAcl(...acls) {
    if (!this.login || !this.acl)
        return false
    if (!acls)
        return true
    return acls.every(acl => this.acl[acl])
}

/**
 * 
 * @returns {false|string}
 */
function getClientName() {
    if (!this.login || !getClient() || !this.clients)
        return false
    const client = this.clients.find(client => client.id == getClient())
    return client ? client.name : false

}

/**
 * Check if the actual token is valid or not
 * @returns {Promise<boolean>} 
 */
function isAuth() {
    const actualToken = getToken()
    return new Promise((res, _error) => {
        const notAuth = () => {
            res(false)
            removeToken()
            removeClient()
        }
        if (!actualToken)
            notAuth()
        else {
            const type = 'userdata'
            getUrl(type)
                .then(([url, method]) => {
                    loading.newLoading()
                    return fetch(url, {
                        method,
                        headers: Object.assign({ 'Content-Type': 'application/json; charset=utf-8' }, authHeader()),
                    })
                }).then(response => {
                    if (response.status === 200)
                        return response.json()
                    notAuth()
                }).then(json => {
                    if (json && json.userData && json.userData.login)
                        res(true)
                    else
                        notAuth()
                }).catch(_err =>
                    notAuth()
                ).finally(() => {
                    loading.removeLoading()
                })
        }
    })
}

/**
 * @typedef {object} userSim3D
 * @property {boolean} login
 * @property {(...acls: any[]) => boolean} getAcl
 * @property {() => false | string} getClientName
 * @property {Promise<boolean>} isAuth
 * @property {Array<clientShort>} clients
 */
/** @type {userSim3D} */
const defaultUser = {
    login: false,
    getAcl,
    getClientName,
    clients: [],
    isAuth: Promise.resolve(false)
}

/** @type {import('svelte/store').Readable<userSim3D>} */
const userStore = derived(token, ($token, set) => {
    if (!$token)
        set(defaultUser)
    else {
        const user = token2user($token.split('.')[1])
        user.getAcl = getAcl
        user.getClientName = getClientName
        user.clients = []
        user.isAuth = isAuth()
        user.isAuth.then(isAuthenticated => {
            if (isAuthenticated)
                getClients().then(clients => {
                    if (clients) {
                        user.clients = clients
                        set(user)
                    }
                })
        })
        set(user)
    }
}, defaultUser)

const login = async ({ user, pass, rememberUser }) => {
    const type = 'login'
    loading.newLoading()
    try {
        const [url,method] = await getUrl(type);
        const response = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json; charset=utf-8' },
            body: JSON.stringify({ user, pass })
        })
        if (response.status === 200) {
            const responseJson = await response.json()
            if (responseJson.userData.clients && responseJson.userData.clients.length === 1)
                saveClient(responseJson.userData.clients[0].id, rememberUser)
            saveToken(responseJson.token, rememberUser)
            return responseJson
        }
        return { status: response.status, message: (await response.json())['response'] }
    } catch (err) {
        alert.newAlert({ message: err.message, type: 'error' })
        return { status: 500, message: err.message }
    } finally {
        loading.removeLoading()
    }

}

const changeClientStore = () => {
    const { subscribe, set } = writable(false)

    const change = () => set(true)

    const changed = () => set(false)

    return { subscribe, change, changed }
}

const changeClient = changeClientStore()

const clientStore = writable(getLocalStorage(CLIENT_STORAGE_NAME))

const defaultClient = {
    id: null,
    name: "",
    features: {
        zeroPriceOrders: false,
        dbOrders: false,
    },
    getFeature: () => false
};
const clientDataStore = derived(clientStore, async ($clientId, set) => {
    if (!$clientId) {
        set(defaultClient)
    }
    else {
        userStore.subscribe(userData => {
            const data = userData.clients.find(c => String(c.id) === String($clientId));
            if (data) {
                data.getFeature = feature => data?.features && data.features[feature]
                set(data);
            }
            else {
                set(defaultClient);
            }
        });
    }
}, defaultClient)

/**
 * Get the current client from the cookie currentClient
 * @returns {string|null} clientId
 */
const getClient = () => getLocalStorage(CLIENT_STORAGE_NAME)

const saveClient = (clientId, rememberUser) => {
    createLocalStorage({ name: CLIENT_STORAGE_NAME, value: clientId });
    clientStore.set(clientId);

    // TODO: habría que ver por qué no se actualizan algunos stores. Es necesario
    // recargar la página para que se reflejen los cambios en algunos sitios
    location.reload();
}

const removeClient = () => {
    clientStore.set(null)
    deleteLocalStorage(CLIENT_STORAGE_NAME)
}

const logout = () => {
    removeToken()
    removeClient()
}

const daysToMilliseconds = days => days * 24 * 60 * 60 * 1000

/**
 * 
 * @returns {{'x-access-token': string}} token
 */
const authHeader = () => ({ 'x-access-token': getToken() ?? '' })

/** @typedef {{id: number, name: string}} clientShort */
/**
 * 
 * @returns {Promise<Array<clientShort>|null>}
 */
const getClients = async () => {
    const type = "clientlist";
    const [url, method] = await getUrl(type);
    try {
        const response = await fetch(url, {
            method,
            headers: Object.assign({ 'Content-Type': 'application/json; charset=utf-8' }, authHeader())
        })
        if (response.status === 200) {
            const { data: clients } = await response.json()
            return clients
        } else {
            alert.newAlert({ message: "Error cargando los clientes del usuario", type: 'error' })
        }
    } catch (err) {
        alert.newAlert({ message: "Error cargando los clientes del usuario", type: 'error' })
    }
    return null
}

export {
    login, logout, userStore, authHeader, saveClient, getClient, removeClient,
    clientStore, changeClient, clientDataStore, token as tokenStore
}